import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Moon, Clock, Bed, User, PawPrint, Wind } from 'lucide-react';
import { SparkleGuide } from './guide/SparkleGuide';
import WhoopConnectionPrompt from '@/components/whoop/WhoopConnectionPrompt';
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";
import { supabase } from '@/integrations/supabase/client';

interface SleepData {
  bedtime: string;
  wake_time: string;
  sleep_duration: number;
  sleep_quality: number;
  shared_bed: boolean;
  pet_in_room: boolean;
  window_open: boolean;
  night_wakings: number;
  sleep_factors: string[];
  sleep_environment: string;
}

interface EnhancedSleepTrackerProps {
  initialData?: Partial<SleepData>;
  onDataChange: (data: SleepData) => void;
}

const SLEEP_FACTORS = [
  'Caffeine after 2pm',
  'Large meal before bed',
  'Alcohol consumption',
  'Screen time before bed',
  'Exercise within 3 hours',
  'Stress/anxiety',
  'Noise disturbance',
  'Room too hot/cold',
  'Uncomfortable mattress',
  'Snoring partner'
];

const SLEEP_ENVIRONMENTS = [
  'Very quiet',
  'Some noise',
  'Noisy environment',
  'White noise/earplugs'
];

// Helper function to calculate duration between two times
const calculateSleepDuration = (bedtime: string, wakeTime: string): number => {
  if (!bedtime || !wakeTime) return 0;
  
  const bed = new Date(`2000-01-01T${bedtime}:00`);
  let wake = new Date(`2000-01-01T${wakeTime}:00`);
  
  // If wake time is earlier than bedtime, assume it's the next day
  if (wake <= bed) {
    wake = new Date(`2000-01-02T${wakeTime}:00`);
  }
  
  const diffMs = wake.getTime() - bed.getTime();
  return diffMs / (1000 * 60 * 60); // Convert to hours
};

const formatDuration = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.floor((hours % 1) * 60);
  return `${h}h ${m}m`;
};

const getSleepQualityLabel = (quality: number): string => {
  if (quality >= 9) return 'Excellent';
  if (quality >= 7) return 'Good';
  if (quality >= 5) return 'Fair';
  if (quality >= 3) return 'Poor';
  return 'Very Poor';
};

const getSleepQualityColor = (quality: number): string => {
  if (quality >= 8) return 'text-accent';
  if (quality >= 6) return 'text-primary';
  return 'text-destructive';
};

export default function EnhancedSleepTracker({ initialData, onDataChange }: EnhancedSleepTrackerProps) {
  const [data, setData] = useState<SleepData>({
    bedtime: '',
    wake_time: '',
    sleep_duration: 0,
    sleep_quality: 7,
    shared_bed: false,
    pet_in_room: false,
    window_open: false,
    night_wakings: 0,
    sleep_factors: [],
    sleep_environment: 'Very quiet',
    ...initialData
  });

  const [isWhoopConnected, setIsWhoopConnected] = useState(false);

  useEffect(() => {
    checkWhoopConnection();
  }, []);

  const checkWhoopConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: connection } = await supabase
        .from('whoop_connections')
        .select('is_active')
        .eq('user_id', user.id)
        .single();

      setIsWhoopConnected(connection?.is_active || false);
    } catch (error) {
      console.error('Error checking WHOOP connection:', error);
    }
  };

  React.useEffect(() => {
    if (data.bedtime && data.wake_time) {
      const duration = calculateSleepDuration(data.bedtime, data.wake_time);
      const updatedData = { ...data, sleep_duration: duration };
      setData(updatedData);
      onDataChange(updatedData);
    }
  }, [data.bedtime, data.wake_time]);

  React.useEffect(() => {
    onDataChange(data);
  }, [data.sleep_quality, data.shared_bed, data.pet_in_room, data.window_open, data.night_wakings, data.sleep_factors, data.sleep_environment]);

  const handleInputChange = (field: keyof SleepData, value: any) => {
    const updatedData = { ...data, [field]: value };
    setData(updatedData);
  };

  const handleSleepFactorChange = (factor: string, checked: boolean) => {
    const newFactors = checked 
      ? [...data.sleep_factors, factor]
      : data.sleep_factors.filter(f => f !== factor);
    handleInputChange('sleep_factors', newFactors);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="text-center mb-6">
          <Moon className="w-12 h-12 text-primary mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <h3 className="text-2xl font-bold">Sleep Tracking</h3>
            <SparkleGuide
              title="How to track your sleep"
              items={[
                { title: 'Log times', description: 'Record your bedtime and wake time to calculate sleep duration automatically.' },
                { title: 'Track quality', description: 'Rate how restorative your sleep felt to spot patterns.' },
                { title: 'Note factors', description: 'Record what affected your sleep (caffeine, stress, etc.) to identify triggers.' },
                { title: 'See correlations', description: 'Over time, discover how sleep affects your mood, energy, and focus.' }
              ]}
            />
          </div>
          <p className="text-muted-foreground">Track your sleep patterns for better insights</p>
        </div>

        {/* Sleep Times */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
              <Bed className="w-4 h-4" />
              Bedtime
            </Label>
            <Input
              type="time"
              value={data.bedtime}
              onChange={(e) => handleInputChange('bedtime', e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" />
              Wake Time
            </Label>
            <Input
              type="time"
              value={data.wake_time}
              onChange={(e) => handleInputChange('wake_time', e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Calculated Duration */}
        {data.sleep_duration > 0 && (
          <div className="text-center mb-6 p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary mb-1">
              {formatDuration(data.sleep_duration)}
            </div>
            <div className="text-sm text-muted-foreground">Total sleep time</div>
            {data.sleep_duration < 6 && (
              <Badge variant="destructive" className="mt-2">
                Below recommended 7-9 hours
              </Badge>
            )}
            {data.sleep_duration >= 7 && data.sleep_duration <= 9 && (
              <Badge variant="default" className="mt-2">
                Within healthy range
              </Badge>
            )}
          </div>
        )}

        {/* Sleep Quality */}
        <div className="mb-6">
          <Label className="text-lg font-semibold mb-4 block">Sleep Quality</Label>
          <div className="px-4">
            <Slider
              value={[data.sleep_quality]}
              onValueChange={(value) => handleInputChange('sleep_quality', value[0])}
              max={10}
              min={1}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-muted-foreground mt-2">
              <span>Very Poor</span>
              <span>Excellent</span>
            </div>
            <div className="text-center mt-3">
              <div className={`text-lg font-semibold ${getSleepQualityColor(data.sleep_quality)}`}>
                {data.sleep_quality}/10 - {getSleepQualityLabel(data.sleep_quality)}
              </div>
            </div>
          </div>
        </div>

        {/* Night Wakings */}
        <div className="mb-6">
          <Label className="text-sm font-medium mb-2 block">
            Night wakings (toilet, noise, etc.)
          </Label>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              min="0"
              max="20"
              value={data.night_wakings}
              onChange={(e) => handleInputChange('night_wakings', parseInt(e.target.value) || 0)}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">times</span>
          </div>
        </div>
      </Card>

      {/* Sleep Environment */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4">Sleep Environment</h4>
        
        {/* Bed Sharing & Pets */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center space-x-3 p-3 rounded-lg border">
            <Checkbox
              id="shared_bed"
              checked={data.shared_bed}
              onCheckedChange={(checked) => handleInputChange('shared_bed', checked)}
            />
            <Label htmlFor="shared_bed" className="flex items-center gap-2 cursor-pointer">
              <User className="w-4 h-4" />
              Shared bed
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-3 rounded-lg border">
            <Checkbox
              id="pet_in_room"
              checked={data.pet_in_room}
              onCheckedChange={(checked) => handleInputChange('pet_in_room', checked)}
            />
            <Label htmlFor="pet_in_room" className="flex items-center gap-2 cursor-pointer">
              <PawPrint className="w-4 h-4" />
              Pet in room
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-3 rounded-lg border">
            <Checkbox
              id="window_open"
              checked={data.window_open}
              onCheckedChange={(checked) => handleInputChange('window_open', checked)}
            />
            <Label htmlFor="window_open" className="flex items-center gap-2 cursor-pointer">
              <Wind className="w-4 h-4" />
              Window open
            </Label>
          </div>
        </div>

        {/* Environment Noise */}
        <div className="mb-6">
          <Label className="text-sm font-medium mb-3 block">Sleep environment</Label>
          <RadioGroup 
            value={data.sleep_environment} 
            onValueChange={(value) => handleInputChange('sleep_environment', value)}
          >
            <div className="grid grid-cols-2 gap-3">
              {SLEEP_ENVIRONMENTS.map((env) => (
                <div key={env} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                  <RadioGroupItem value={env} id={env} />
                  <Label htmlFor={env} className="font-medium cursor-pointer flex-1">
                    {env}
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </div>
      </Card>

      {/* Sleep Disruptors */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4">Potential Sleep Disruptors</h4>
        <p className="text-sm text-muted-foreground mb-4">
          Select any factors that may have affected your sleep:
        </p>
        
        <div className="grid grid-cols-2 gap-3">
          {SLEEP_FACTORS.map((factor) => (
            <div key={factor} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50">
              <Checkbox
                id={factor}
                checked={data.sleep_factors.includes(factor)}
                onCheckedChange={(checked) => handleSleepFactorChange(factor, checked as boolean)}
              />
              <Label htmlFor={factor} className="text-sm cursor-pointer flex-1">
                {factor}
              </Label>
            </div>
          ))}
        </div>

        {data.sleep_factors.length > 0 && (
          <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
            <p className="text-sm text-foreground">
              <strong>Tip:</strong> Over time, you'll see patterns between these factors and your sleep quality. 
              Try eliminating one factor at a time to see what improves your rest.
            </p>
          </div>
        )}
      </Card>

      {/* Sleep Insights */}
      <Card className="p-6 bg-gradient-therapy border-soft-accent/30">
        <h4 className="font-semibold mb-3 text-primary">💡 Sleep Insight</h4>
        <p className="text-sm text-foreground">
          {data.sleep_quality >= 8 
            ? "Excellent sleep! Your sleep environment and habits are supporting quality rest."
            : data.sleep_quality >= 6
            ? "Good sleep foundation. Consider tracking which factors correlate with your best nights."
            : data.night_wakings > 3
            ? "Multiple night wakings can fragment sleep. Consider what might be causing frequent awakenings."
            : data.sleep_duration < 6
            ? "Short sleep duration detected. Even small increases in sleep time can improve energy and mood."
            : "Several factors might be impacting your sleep quality. Use the insights feature to identify patterns."
          }
        </p>
      </Card>

      {/* WHOOP Connection Prompt */}
      <WhoopConnectionPrompt context="sleep" isConnected={isWhoopConnected} />

      {/* Contextual notification prompt */}
      <ContextualNotificationPrompt context="sleep" />
    </div>
  );
}